// PanelSkeletons.js — every per-panel loading skeleton for Local Intelligence
// (work-page-fixes Item 1). Each mimics its real panel's layout using the
// app's Skeleton shimmer atoms, so a load reads as "already here"
// panel-by-panel instead of one page-level placeholder.
import React from 'react';
import { Skeleton, SkeletonCard, SkeletonMetricCard } from '../../shared/Skeleton';
import { CARD_PAD } from '../liStyles';

// Five stat tiles — mirrors the MetricCardShell tile grid (Tier 2).
export const StatTilesSkeleton = () => (
  <div className="grid [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))] gap-4" role="status" aria-label="Loading" aria-live="polite">
    {Array.from({ length: 5 }).map((_, i) => <SkeletonMetricCard key={i} />)}
  </div>
);

// The combined "What Does the Guest Need?" panel — need-tile grid on the left,
// answer-card column on the right. Same ring + two-column grid as the real panel.
export const AskPanelSkeleton = () => (
  <section className={`${CARD_PAD} ring-1 ring-[#32B1FF]/30`} aria-label="What Does the Guest Need?">
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1px_1fr] gap-6 items-stretch">
      {/* Left — title block + need-tile grid + filter rows */}
      <div className="min-w-0 space-y-4">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-3 w-72" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
        </div>
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>
      {/* Divider — hidden on small screens, same as the real panel */}
      <div className="hidden lg:block bg-gray-200 dark:bg-gray-800" />
      {/* Right — results header + answer cards */}
      <div className="min-w-0 space-y-3">
        <Skeleton className="h-4 w-40" />
        {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} lines={3} />)}
      </div>
    </div>
  </section>
);

// Places Open Right Now — a column of card-shaped blocks.
export const RightNowSkeleton = () => (
  <div className="space-y-3" role="status" aria-label="Loading" aria-live="polite">
    <Skeleton className="h-5 w-44" />
    {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} lines={2} />)}
  </div>
);

// Essential Services Nearby (Help right now) — a two-column grid of card shapes.
export const HelpRailSkeleton = () => (
  <div className="space-y-3" role="status" aria-label="Loading" aria-live="polite">
    <Skeleton className="h-5 w-40" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={2} />)}
    </div>
  </div>
);

// What Fits the Moment? — a tile grid of placeholder blocks.
export const OccasionsSkeleton = () => (
  <div className="space-y-3" role="status" aria-label="Loading" aria-live="polite">
    <Skeleton className="h-5 w-48" />
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
    </div>
  </div>
);

// Booking Requests — a slim strip shape.
export const RequestsSkeleton = () => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" role="status" aria-label="Loading" aria-live="polite">
    <Skeleton className="h-5 w-44" />
    <Skeleton className="h-12 w-full rounded-lg mt-3" />
  </div>
);

// Planning a Longer Stay — a slim strip skeleton matching the form's one-row
// layout (date-picker shape, nights-dropdown shape, chip shapes, button shape)
// so it reads as "loading" alongside the other per-panel skeletons while
// fn_li_page is in flight. The real StayPlan form is gated behind the page-data
// (usable) block, so it never renders with empty party_options before the RPC
// resolves (work-page-fixes Item 2). Same card surface as the real panel.
export const StayPlanSkeleton = () => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-4" role="status" aria-label="Loading" aria-live="polite">
    {/* Title + description line. */}
    <div className="space-y-2">
      <Skeleton className="h-5 w-56" />
      <Skeleton className="h-3 w-72" />
    </div>
    {/* Form — Arriving date + Nights dropdown side by side, a row of party chip
        shapes, then the Plan-the-stay button right-aligned, mirroring the real
        form's stacked layout (StayPlan.js). */}
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <Skeleton className="h-10 w-40 rounded-lg" />
        <Skeleton className="h-10 w-24 rounded-lg" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-7 w-20 rounded-full" />)}
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
    </div>
    <span className="sr-only">Loading…</span>
  </div>
);

// Results-area skeleton for the right column of the combined panel (work-page-fixes
// Item 2). Shown while fn_li_answer is in flight — including the auto-select case on
// initial page load — so the right side reads as "loading results" instead of
// flashing the empty-state prompt between fn_li_page resolving and fn_li_answer
// resolving. Mimics the results header (need label · when · N places) plus a stack
// of AnswerCard-shaped blocks; the left column (filters) renders as soon as
// fn_li_page data is available, so only the right side waits.
export const ResultsSkeleton = () => (
  <div className="space-y-3" role="status" aria-label="Loading results" aria-live="polite">
    <Skeleton className="h-4 w-48" />
    {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} lines={3} />)}
    <span className="sr-only">Loading results…</span>
  </div>
);
